<template>
    <div class="client-systems-testing">
        <div class="client-systems-testing__switch">
            <div class="client-systems-testing__switch--test">
                {{ toSentenceCase($t('admin.clientSystemsTesting.systemToTest')) }}:
            </div>
            <v-btn-toggle v-model="clientSystem" mandatory>
                <v-btn :value="clientSystems.forecasting">
                    {{ toSentenceCase($t('admin.clientSystemsTesting.forecastingSystem')) }}
                </v-btn>
                <v-btn :value="clientSystems.execution">
                    {{ toSentenceCase($t('admin.clientSystemsTesting.executionSystem')) }}
                </v-btn>
            </v-btn-toggle>
        </div>
        <div class="client-systems-testing__headers">
            <div class="client-systems-testing__headers--input">
                {{ $t('admin.clientSystemsTesting.jsonInput') }}
            </div>
            <div class="client-systems-testing__headers--input">
                {{ $t('admin.clientSystemsTesting.jsonResponse') }}
            </div>
        </div>
        <div class="client-systems-testing__editors">
            <JsonEditorVue
                v-model="json[clientSystem]"
                mode="text"
                :stringified="false"
                :on-change="onInputChange"
                class="client-systems-testing__editors--input"
            />
            <JsonEditorVue
                :model-value="!isEmpty(errorJson) ? errorJson : responseJson"
                mode="text"
                :stringified="false"
                :read-only="true"
                :main-menu-bar="true"
                :navigation-bar="true"
                :status-bar="true"
                class="client-systems-testing__editors--response"
            />
        </div>
        <div class="client-systems-testing__buttons">
            <v-btn primary depressed @click="transformPromo">
                {{ toSentenceCase($t('actions.transform')) }}
            </v-btn>
            <v-divider vertical class="client-systems-testing__buttons--divider" />
            <v-btn
                primary
                depressed
                :disabled="disabled || isEmpty(transformedPromotion)"
                @click="submit"
            >
                {{ toSentenceCase($t('actions.submit')) }}
            </v-btn>
            <v-btn
                primary
                depressed
                class="client-systems-testing__buttons--clear"
                :disabled="disabled"
                @click="clear"
            >
                {{ toSentenceCase($t('actions.clear')) }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import to from 'await-to-js';
import clientSystems from '@enums/client-systems';
import JsonEditorVue from 'json-editor-vue';
import { isEmpty } from 'lodash';

export default {
    components: {
        JsonEditorVue,
    },
    data() {
        return {
            clientSystems,
            clientSystem: clientSystems.forecasting,
            json: {
                [clientSystems.forecasting]: {
                    promotionId: '',
                },
                [clientSystems.execution]: {
                    promotionId: '',
                },
            },
            hasInputErrors: {
                [clientSystems.forecasting]: false,
                [clientSystems.execution]: false,
            },
        };
    },
    computed: {
        disabled() {
            return this.hasInputErrors[this.clientSystem];
        },
        responseJson() {
            return this.$store.state[this.clientSystem].jsonPromotionResponse;
        },
        errorJson() {
            return this.$store.state[this.clientSystem].jsonPromotionError;
        },
        transformedPromotion() {
            return this.$store.state[this.clientSystem].transformedPromotion;
        },
    },
    methods: {
        isEmpty,
        onInputChange(_content, _previousContent, status) {
            this.hasInputErrors[this.clientSystem] = Boolean(status?.contentErrors);
        },
        async submit() {
            await this.$store.dispatch(`${this.clientSystem}/submitJson`, {
                json: this.json[this.clientSystem],
            });
        },
        getJson() {
            const currentJson = this.json[this.clientSystem];
            if (currentJson === undefined || currentJson === null) {
                return null;
            }
            return currentJson;
        },
        async transformPromo() {
            const [err, transformedPromotion] = await to(
                this.$store.dispatch(`${this.clientSystem}/transformPromotion`, {
                    json: this.getJson(),
                })
            );
            if (!err && transformedPromotion) {
                this.json[this.clientSystem] = transformedPromotion;
                this.hasInputErrors[this.clientSystem] = false;
            }
        },

        clear() {
            this.json[this.clientSystem] = {
                promotionId: '',
            };
            this.hasInputErrors[this.clientSystem] = false;
            this.$store.dispatch(`${this.clientSystem}/clearTestingValues`);
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.client-systems-testing {
    height: calc(100vh - #{$main-navbar-height} - #{$main-footer-height});
    max-height: calc(100% - #{$main-navbar-height} - #{$main-footer-height});
    min-height: 0;
    width: 100%;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 0;
    box-sizing: border-box;

    &__switch,
    &__buttons,
    &__headers {
        padding: 0.5rem;
        display: flex;

        flex-direction: row;
        font-size: 1.4rem;
        font-weight: 600;
        background-color: white;

        &--test {
            padding-right: 1rem;
        }
    }

    &__headers {
        > * {
            width: 50%;
        }
    }

    &__buttons {
        border-top: 1px $promo-grey-2 solid;
        flex: 0 0 auto;
        position: relative;
        z-index: 2;

        &--divider {
            margin-left: 0.2rem;
            margin-right: 0.2rem;
        }

        &--clear {
            margin-left: auto;
        }
    }

    &__editors {
        display: flex;
        flex: 1 1 auto;
        min-height: 0;
        max-height: 100%;
        overflow: hidden;
    }

    &__editors--input,
    &__editors--response {
        flex: 0 0 50%;
        width: 50%;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
    }

    :deep(.jse-menu) {
        background-color: $promo-primary-colour;
        border: unset;
    }

    :deep(.jse-main) {
        border: unset;
        height: 100%;
        max-height: 100%;
        min-height: 0;
        overflow: hidden;
        background-color: white;
    }

    :deep(.jse-contents),
    :deep(.jse-text),
    :deep(.cm-editor),
    :deep(.cm-scroller) {
        min-height: 0;
        height: 100%;
        max-height: 100%;
    }

    :deep(.jse-contents) {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    :deep(.cm-scroller) {
        overflow: auto !important;
    }

    :deep(.client-systems-testing__editors--response .jse-main) {
        border-left: 2px $promo-primary-colour solid;
        height: 100%;
        background-color: white;
    }

    :deep(.jse-navigation),
    :deep(.jse-status) {
        display: none;
    }

    :deep(.jse-value),
    :deep(.jse-key),
    :deep(.jse-separator) {
        line-height: 2rem;
    }
}
</style>
